/**
 * PersonalInfoTable Component
 * 
 * Bureau comparison table for personal information.
 * Displays data in columns: Field | TransUnion | Experian | Equifax
 * with support for selection and dispute actions.
 */

import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  User,
  MapPin,
  Briefcase,
  Calendar,
  Users,
  Plus,
  CheckCircle,
  Filter,
  Gavel,
  X,
  Home,
  Info,
  AlertTriangle
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { cn } from '../../lib/utils';

// ============================================
// EMPLOYER RECOMMENDATION CONSTANTS
// ============================================
const EMPLOYER_EDUCATION_COPY = 
  "Employer information is often used as part of credit-file matching and verification. " +
  "If employer data is outdated, inconsistent, unnecessary, or not confirmed as current, " +
  "it can weaken the personal-information cleanup strategy and make negative-item disputes harder to isolate. " +
  "We recommend disputing employer information with the rest of your personal information unless you intentionally want it to remain.";
import { useDisputeTray, DISPUTE_CATEGORIES } from '../../context/DisputeTrayContext';

// Field type definitions with icons and colors
const FIELD_CONFIG = {
  name: { 
    label: 'Name', 
    icon: User, 
    color: 'bg-emerald-50 text-emerald-600',
    order: 1 
  },
  also_known_as: { 
    label: 'Also Known As', 
    icon: Users, 
    color: 'bg-amber-50 text-amber-600',
    order: 2 
  },
  former: { 
    label: 'Former Name / Alias', 
    icon: Users, 
    color: 'bg-amber-50 text-amber-600',
    order: 3 
  },
  date_of_birth: { 
    label: 'Date of Birth', 
    icon: Calendar, 
    color: 'bg-emerald-50 text-emerald-600',
    order: 4 
  },
  current_addresses: { 
    label: 'Current Address(es)', 
    icon: Home, 
    color: 'bg-green-50 text-green-600',
    order: 5 
  },
  previous_addresses: { 
    label: 'Previous Address(es)', 
    icon: MapPin, 
    color: 'bg-slate-50 text-slate-600',
    order: 6 
  },
  employers: { 
    label: 'Employers', 
    icon: Briefcase, 
    color: 'bg-amber-50 text-amber-600',
    order: 7 
  }
};

const BUREAU_NAMES = ['TransUnion', 'Experian', 'Equifax'];

/**
 * Empty State Component - Themed
 */
const EmptyState = ({ title, description, theme = {} }) => (
  <div className="text-center py-12 px-6">
    <div 
      className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
      style={{ 
        background: theme?.emptyIcon?.bg || 'rgba(15,172,108,0.10)',
        border: `1px solid ${theme?.emptyIcon?.border || 'rgba(15,172,108,0.12)'}`,
      }}
    >
      <User size={28} style={{ color: theme?.emptyIcon?.color || '#0D9F64' }} />
    </div>
    <h3 
      className="text-lg font-bold mb-2"
      style={{ color: theme?.text?.primary || '#111827' }}
    >
      {title}
    </h3>
    <p 
      className="text-sm max-w-md mx-auto"
      style={{ color: theme?.text?.muted || '#6B7280' }}
    >
      {description}
    </p>
  </div>
);

/**
 * Bulk Action Bar Component
 */
const BulkActionBar = ({ selectedCount, onDispute, onClear }) => {
  if (selectedCount === 0) return null;
  
  return (
    <div className="sticky bottom-0 left-0 right-0 p-4 bg-stone-800 rounded-xl shadow-lg border border-slate-700 flex items-center justify-between animate-in slide-in-from-bottom-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-emerald-500 flex items-center justify-center">
          <CheckCircle size={20} className="text-white" />
        </div>
        <div>
          <p className="text-white font-medium">{selectedCount} item{selectedCount === 1 ? '' : 's'} selected</p>
          <p className="text-slate-400 text-sm">Ready for bulk action</p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          onClick={onClear}
          className="text-slate-300 hover:text-white hover:bg-slate-700"
          data-testid="clear-personal-info-selection"
        >
          <X size={16} className="mr-1" />
          Clear
        </Button>
        <Button
          onClick={onDispute}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="bulk-dispute-personal-info-btn"
        >
          <Gavel size={16} className="mr-2" />
          Dispute Selected
        </Button>
      </div>
    </div>
  );
};

/**
 * Format a value for display in a bureau cell
 * Handles arrays (multiple values) and null/empty values
 */
const formatCellValue = (value) => {
  if (value === null || value === undefined) {
    return null;
  }
  
  if (Array.isArray(value)) {
    const filtered = value.filter(v => v && v.trim());
    if (filtered.length === 0) return null;
    return filtered;
  }
  
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || null;
  }
  
  return String(value);
};

/**
 * Format an address string into structured lines
 * Input: "2307 MAGDALINA, DR 111, PUNTA GORDA, FL, 33950"
 * Output: ["2307 Magdalina Dr", "Unit 111", "Punta Gorda, FL 33950"]
 */
const formatAddress = (addressStr) => {
  if (!addressStr || typeof addressStr !== 'string') return null;
  
  // Normalize the address string
  let addr = addressStr
    .replace(/\xa0/g, ' ')  // Replace non-breaking spaces
    .replace(/\s+/g, ' ')   // Normalize whitespace
    .trim();
  
  // Split by comma and clean up parts
  const parts = addr.split(',').map(p => p.trim()).filter(p => p);
  
  if (parts.length === 0) return null;
  
  // Street suffixes
  const streetSuffixes = {
    'DR': 'Dr', 'DRIVE': 'Dr',
    'ST': 'St', 'STREET': 'St',
    'AVE': 'Ave', 'AVENUE': 'Ave', 'AV': 'Ave',
    'RD': 'Rd', 'ROAD': 'Rd',
    'LN': 'Ln', 'LANE': 'Ln',
    'CT': 'Ct', 'COURT': 'Ct',
    'BLVD': 'Blvd', 'BOULEVARD': 'Blvd',
    'WAY': 'Way',
    'PL': 'Pl', 'PLACE': 'Pl',
    'CIR': 'Cir', 'CIRCLE': 'Cir',
    'TER': 'Ter', 'TERRACE': 'Ter',
    'PKWY': 'Pkwy', 'PARKWAY': 'Pkwy',
    'HWY': 'Hwy', 'HIGHWAY': 'Hwy',
    'TRL': 'Trl', 'TRAIL': 'Trl',
  };
  
  // State abbreviations
  const statePattern = /^[A-Z]{2}$/;
  
  // ZIP code pattern
  const zipPattern = /^\d{5}(-\d{4})?$/;
  
  let streetNumber = '';
  let streetName = '';
  let streetSuffix = '';
  let unitNumber = '';
  let cityParts = [];
  let state = '';
  let zip = '';
  
  // Process each part
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i].trim();
    const partUpper = part.toUpperCase();
    
    // Check if this is a ZIP code
    if (zipPattern.test(part)) {
      zip = part;
      continue;
    }
    
    // Check if this is a state abbreviation (2 letters only)
    if (statePattern.test(partUpper) && part.length === 2) {
      state = partUpper;
      continue;
    }
    
    // Check if this part contains state + ZIP together (e.g., "FL 33950")
    const stateZipMatch = part.match(/^([A-Z]{2})\s+(\d{5}(-\d{4})?)$/i);
    if (stateZipMatch) {
      state = stateZipMatch[1].toUpperCase();
      zip = stateZipMatch[2];
      continue;
    }
    
    // First part is usually street number + name
    if (i === 0) {
      // Check for street number at the beginning
      const streetMatch = part.match(/^(\d+)\s+(.+)$/);
      if (streetMatch) {
        streetNumber = streetMatch[1];
        streetName = streetMatch[2];
      } else {
        streetName = part;
      }
      continue;
    }
    
    // Check if this part is a street suffix + unit number (e.g., "DR 111" or "DR UNIT 111")
    const suffixUnitMatch = part.match(/^(DR|ST|AVE?|RD|LN|CT|BLVD|WAY|PL|CIR|TER|PKWY|HWY|TRL)\s+(?:(APT|UNIT|SUITE|STE|#|BLDG)?\s*)?(\d+\w*)$/i);
    if (suffixUnitMatch) {
      streetSuffix = streetSuffixes[suffixUnitMatch[1].toUpperCase()] || suffixUnitMatch[1];
      unitNumber = suffixUnitMatch[3];
      continue;
    }
    
    // Check if this is just a street suffix (e.g., "DR")
    if (streetSuffixes[partUpper]) {
      streetSuffix = streetSuffixes[partUpper];
      continue;
    }
    
    // Check if this is a unit (e.g., "APT 208" or just "208" after street suffix)
    const unitMatch = part.match(/^(APT|UNIT|SUITE|STE|#|BLDG|BUILDING|FLOOR|FL)?\s*(\d+\w*)$/i);
    if (unitMatch && unitMatch[2]) {
      // Only treat as unit if we already have a street suffix or if it has a unit prefix
      if (streetSuffix || unitMatch[1]) {
        unitNumber = unitMatch[2];
        continue;
      }
    }
    
    // Check for AVE APT pattern (e.g., "AVE APT 208")
    const aveAptMatch = part.match(/^(AVE?|DR|ST|RD|LN|CT|BLVD)\s+(APT|UNIT|SUITE|STE)\s+(\d+\w*)$/i);
    if (aveAptMatch) {
      streetSuffix = streetSuffixes[aveAptMatch[1].toUpperCase()] || aveAptMatch[1];
      unitNumber = aveAptMatch[3];
      continue;
    }
    
    // If it's a word (not a number), treat as part of city name
    // This handles cities like "PORT, CHARLOTTE" -> "Port Charlotte"
    // or "ORMOND, BEACH" -> "Ormond Beach"
    // or "PT CHARLOTTE" -> "Pt Charlotte"
    if (!/^\d+\w*$/.test(part)) {
      cityParts.push(part);
    }
  }
  
  // Build the formatted lines
  const lines = [];
  
  // Line 1: Street address
  let streetLine = '';
  if (streetNumber) streetLine += streetNumber + ' ';
  if (streetName) {
    // Title case the street name
    streetLine += streetName
      .toLowerCase()
      .replace(/\b\w/g, c => c.toUpperCase());
  }
  if (streetSuffix) streetLine += ' ' + streetSuffix;
  if (streetLine.trim()) lines.push(streetLine.trim());
  
  // Line 2: Unit (if present)
  if (unitNumber) {
    lines.push('Unit ' + unitNumber);
  }
  
  // Line 3: City, State ZIP
  let cityStateZip = '';
  if (cityParts.length > 0) {
    // Join city parts and title case
    const fullCity = cityParts.join(' ')
      .toLowerCase()
      .replace(/\b\w/g, c => c.toUpperCase())
      // Handle "Pt" -> keep as "Pt" (abbreviation for Port)
      .replace(/\bPt\b/g, 'Pt');
    cityStateZip += fullCity;
  }
  if (state) {
    if (cityStateZip) cityStateZip += ', ';
    cityStateZip += state;
  }
  if (zip) {
    if (cityStateZip) cityStateZip += ' ';
    cityStateZip += zip;
  }
  if (cityStateZip) lines.push(cityStateZip);
  
  return lines.length > 0 ? lines : [addr];
};

/**
 * Address Cell Component - renders formatted address with line breaks
 * Now supports reportedDate as subtle metadata
 */
const AddressCell = ({ value, reportedDate, theme = {} }) => {
  const formatted = formatCellValue(value);
  
  if (formatted === null) {
    return <span style={{ color: theme?.text?.muted || '#6B7280', fontStyle: 'italic' }}>—</span>;
  }
  
  // Handle array of addresses
  if (Array.isArray(formatted)) {
    return (
      <div className="space-y-3">
        {formatted.map((addr, idx) => {
          const lines = formatAddress(addr);
          return (
            <div 
              key={idx} 
              className="text-sm leading-snug"
              style={{ color: theme?.text?.primary || '#111827' }}
            >
              {lines && lines.map((line, lineIdx) => (
                <div key={lineIdx}>{line}</div>
              ))}
            </div>
          );
        })}
      </div>
    );
  }
  
  // Handle single address string
  const lines = formatAddress(formatted);
  return (
    <div>
      <div 
        className="text-sm leading-snug"
        style={{ color: theme?.text?.primary || '#111827' }}
      >
        {lines && lines.map((line, idx) => (
          <div key={idx}>{line}</div>
        ))}
      </div>
      {/* Show reported date as subtle metadata */}
      {reportedDate && (
        <div 
          className="mt-1 text-xs italic"
          style={{ color: theme?.text?.muted || '#9CA3AF' }}
          data-testid="address-reported-date"
        >
          Reported: {reportedDate}
        </div>
      )}
    </div>
  );
};

/**
 * Bureau Cell Component - renders values for a single bureau
 * Now accepts reportedDate for address fields
 */
const BureauCell = ({ value, bureau, fieldKey, reportedDate, theme = {} }) => {
  // Use AddressCell for address fields
  if (fieldKey === 'current_addresses' || fieldKey === 'previous_addresses' || fieldKey?.startsWith('previous_address_')) {
    return <AddressCell value={value} reportedDate={reportedDate} theme={theme} />;
  }
  
  const formatted = formatCellValue(value);
  
  if (formatted === null) {
    return (
      <span style={{ color: theme?.text?.muted || '#6B7280', fontStyle: 'italic' }}>—</span>
    );
  }
  
  if (Array.isArray(formatted)) {
    return (
      <div className="space-y-1">
        {formatted.map((v, idx) => (
          <p 
            key={idx} 
            className="text-sm leading-relaxed"
            style={{ color: theme?.text?.primary || '#111827' }}
          >
            {v}
          </p>
        ))}
      </div>
    );
  }
  
  return (
    <p 
      className="text-sm leading-relaxed"
      style={{ color: theme?.text?.primary || '#111827' }}
    >
      {formatted}
    </p>
  );
};

/**
 * Helper to check if a row has employer data in any bureau
 */
const hasEmployerData = (row) => {
  if (!row || !row.values) return false;
  const { TransUnion, Experian, Equifax } = row.values;
  
  const checkValue = (val) => {
    if (!val) return false;
    if (typeof val === 'string') return val.trim().length > 0 && val !== '—';
    if (Array.isArray(val)) return val.some(v => v && String(v).trim().length > 0 && v !== '—');
    return false;
  };
  
  return checkValue(TransUnion) || checkValue(Experian) || checkValue(Equifax);
};

/**
 * Transform bureau_data into rows for the comparison table
 */
const transformBureauDataToRows = (bureauData) => {
  if (!bureauData) return [];
  
  const rows = [];
  
  // Process each field in order
  Object.entries(FIELD_CONFIG)
    .sort(([, a], [, b]) => a.order - b.order)
    .forEach(([fieldKey, config]) => {
      // Skip previous_addresses if we have previous_address_items (new format)
      if (fieldKey === 'previous_addresses' && bureauData.previous_address_items?.length > 0) {
        return;
      }
      
      const fieldData = bureauData[fieldKey];
      if (!fieldData) return;
      
      // Check if any bureau has data for this field
      const hasAnyData = BUREAU_NAMES.some(bureau => {
        const val = fieldData[bureau];
        if (val === null || val === undefined) return false;
        if (Array.isArray(val)) return val.length > 0;
        if (typeof val === 'string') return val.trim().length > 0;
        return true;
      });
      
      if (!hasAnyData) return;
      
      rows.push({
        id: `pi-${fieldKey}`,
        fieldKey,
        label: config.label,
        icon: config.icon,
        color: config.color,
        values: {
          TransUnion: fieldData.TransUnion || fieldData.transunion || fieldData.tu,
          Experian: fieldData.Experian || fieldData.experian || fieldData.ex,
          Equifax: fieldData.Equifax || fieldData.equifax || fieldData.eq
        }
      });
    });
  
  // Add separate rows for each previous address (new format)
  const prevAddrItems = bureauData.previous_address_items || [];
  prevAddrItems.forEach((item, idx) => {
    // Check if any bureau has data for this address
    const hasAnyData = BUREAU_NAMES.some(bureau => {
      const val = item[bureau];
      return val && String(val).trim().length > 0;
    });
    
    if (!hasAnyData) return;
    
    rows.push({
      id: `pi-previous_address_${item.index || idx + 1}`,
      fieldKey: `previous_address_${item.index || idx + 1}`,
      label: `Previous Address ${item.index || idx + 1}`,
      icon: MapPin,
      color: 'bg-amber-100 text-amber-700',
      isPreviousAddress: true,
      addressIndex: item.index || idx + 1,
      values: {
        TransUnion: item.TransUnion || null,
        Experian: item.Experian || null,
        Equifax: item.Equifax || null
      },
      // Include reported date metadata per bureau (if present)
      reportedDates: {
        TransUnion: item.TransUnion_reported_date || null,
        Experian: item.Experian_reported_date || null,
        Equifax: item.Equifax_reported_date || null
      }
    });
  });
  
  return rows;
};

/**
 * Main PersonalInfoTable Component - Bureau Comparison View
 */
export const PersonalInfoTable = ({ personalInfo = {}, addresses = [], theme = {} }) => {
  // Navigation for dispute wizard
  const navigate = useNavigate();
  
  // Use dispute tray for multi-item selection
  const { 
    toggleItem, 
    isItemSelected, 
    CATEGORIES 
  } = useDisputeTray();
  
  // Local state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [bureauFilter, setBureauFilter] = useState('all');
  
  // Transform bureau_data into table rows
  const allRows = useMemo(() => {
    const bureauData = personalInfo?.bureau_data;
    if (!bureauData) return [];
    return transformBureauDataToRows(bureauData);
  }, [personalInfo]);
  
  // Filter rows based on search and filters
  const filteredRows = useMemo(() => {
    let filtered = allRows;
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(row => {
        // Search in label
        if (row.label.toLowerCase().includes(query)) return true;
        // Search in values
        return BUREAU_NAMES.some(bureau => {
          const val = row.values[bureau];
          if (!val) return false;
          if (Array.isArray(val)) {
            return val.some(v => v?.toLowerCase().includes(query));
          }
          return String(val).toLowerCase().includes(query);
        });
      });
    }
    
    // Type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(row => row.fieldKey === typeFilter);
    }
    
    // Bureau filter - only show rows where that bureau has data
    if (bureauFilter !== 'all') {
      filtered = filtered.filter(row => {
        const val = row.values[bureauFilter];
        if (!val) return false;
        if (Array.isArray(val)) return val.length > 0;
        if (typeof val === 'string') return val.trim().length > 0;
        return true;
      });
    }
    
    return filtered;
  }, [allRows, searchQuery, typeFilter, bureauFilter]);
  
  // Selection handlers
  const isAllSelected = filteredRows.length > 0 && 
    filteredRows.every(row => selectedIds.has(row.id));
  
  const toggleSelectAll = useCallback(() => {
    if (isAllSelected) {
      setSelectedIds(new Set());
      // Also remove from tray
      filteredRows.forEach(row => {
        if (isItemSelected(CATEGORIES.PERSONAL_INFO, row.id)) {
          toggleItem(CATEGORIES.PERSONAL_INFO, row);
        }
      });
    } else {
      setSelectedIds(new Set(filteredRows.map(row => row.id)));
      // Also add to tray
      filteredRows.forEach(row => {
        if (!isItemSelected(CATEGORIES.PERSONAL_INFO, row.id)) {
          toggleItem(CATEGORIES.PERSONAL_INFO, row);
        }
      });
    }
  }, [isAllSelected, filteredRows, toggleItem, isItemSelected, CATEGORIES]);
  
  const toggleSelect = useCallback((row) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(row.id)) {
        next.delete(row.id);
      } else {
        next.add(row.id);
      }
      return next;
    });
    // Also toggle in tray
    toggleItem(CATEGORIES.PERSONAL_INFO, row);
  }, [toggleItem, CATEGORIES]);
  
  const clearSelection = useCallback(() => {
    // Remove from tray
    filteredRows.forEach(row => {
      if (selectedIds.has(row.id) && isItemSelected(CATEGORIES.PERSONAL_INFO, row.id)) {
        toggleItem(CATEGORIES.PERSONAL_INFO, row);
      }
    });
    setSelectedIds(new Set());
  }, [filteredRows, selectedIds, toggleItem, isItemSelected, CATEGORIES]);
  
  // Handle single dispute - navigate to Dispute Wizard with Data Breach method
  const handleDisputeSingle = useCallback((row) => {
    // Build the personal info item for the wizard
    const personalInfoItem = {
      id: row.id,
      field: row.fieldKey,
      fieldLabel: row.label,
      type: 'personal_info',
      values: row.values,
      // Extract which bureaus have data for this item
      bureaus: BUREAU_NAMES.filter(bureau => {
        const val = row.values[bureau];
        return val && (Array.isArray(val) ? val.length > 0 : true);
      }),
    };
    
    // Navigate to Dispute Wizard with Data Breach method
    navigate('/dispute-wizard/factual/select-items', { 
      state: { 
        preloadedItems: {
          inquiries: [],
          accounts: [],
          publicRecords: [],
          personalInfo: [personalInfoItem],
        },
        fromSmartRouting: true,
        classification: 'data_breach',
        recommendedStrategy: {
          title: 'Data Breach Method',
          key: 'data_breach_method',
          reason: `Personal Information dispute (${row.label}). Personal Information defaults to Data Breach Method.`,
        },
        selectedMethod: 'data_breach',
        disputeType: 'personal_info',
      } 
    });
  }, [navigate]);
  
  // Handle bulk dispute - navigate to Dispute Wizard with Data Breach method
  const handleBulkDispute = useCallback(() => {
    // Build personal info items from selected rows
    const personalInfoItems = filteredRows
      .filter(row => selectedIds.has(row.id))
      .map(row => ({
        id: row.id,
        field: row.fieldKey,
        fieldLabel: row.label,
        type: 'personal_info',
        values: row.values,
        bureaus: BUREAU_NAMES.filter(bureau => {
          const val = row.values[bureau];
          return val && (Array.isArray(val) ? val.length > 0 : true);
        }),
      }));
    
    if (personalInfoItems.length === 0) return;
    
    // Navigate to Dispute Wizard with Data Breach method
    navigate('/dispute-wizard/factual/select-items', { 
      state: { 
        preloadedItems: {
          inquiries: [],
          accounts: [],
          publicRecords: [],
          personalInfo: personalInfoItems,
        },
        fromSmartRouting: true,
        classification: 'data_breach',
        recommendedStrategy: {
          title: 'Data Breach Method',
          key: 'data_breach_method',
          reason: `${personalInfoItems.length} Personal Information item${personalInfoItems.length === 1 ? '' : 's'} selected. Personal Information defaults to Data Breach Method.`,
        },
        selectedMethod: 'data_breach',
        disputeType: 'personal_info_bulk',
      } 
    });
    
    // Clear local selection
    setSelectedIds(new Set());
  }, [filteredRows, selectedIds, navigate]);
  
  // Count rows by type for filter display
  const typeCounts = useMemo(() => {
    const counts = {};
    allRows.forEach(row => {
      counts[row.fieldKey] = (counts[row.fieldKey] || 0) + 1;
    });
    return counts;
  }, [allRows]);
  
  if (allRows.length === 0) {
    return (
      <EmptyState
        title="No Personal Information Found"
        description="No personal information was extracted from your credit report."
        theme={theme}
      />
    );
  }
  
  return (
    <div className="space-y-4" data-testid="personal-info-table">
      {/* Legacy Unattributed Warning */}
      {personalInfo?.bureau_data?.legacy_unattributed && (
        <div 
          className="flex items-center gap-3 p-4 rounded-xl"
          style={{
            background: 'rgba(245, 158, 11, 0.1)',
            border: '1px solid rgba(245, 158, 11, 0.3)',
          }}
          data-testid="legacy-attribution-warning"
        >
          <div className="flex-shrink-0">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 6V10M10 14H10.01M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div>
            <p className="text-sm font-medium" style={{ color: '#B45309' }}>
              Legacy Report - Bureau Attribution Unavailable
            </p>
            <p className="text-xs mt-0.5" style={{ color: '#92400E' }}>
              This report was imported before bureau-level personal information parsing was available. 
              Address data may not accurately reflect which bureau reported each value.
            </p>
          </div>
        </div>
      )}
      
      {/* Filters Row */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex flex-wrap items-center gap-3">
          {/* Search */}
          <div 
            className="relative flex items-center h-10 px-3 rounded-xl w-56"
            style={{
              background: theme?.input?.bg || 'rgba(255,255,255,0.72)',
              border: `1px solid ${theme?.input?.border || 'rgba(15,172,108,0.12)'}`,
              backdropFilter: 'blur(12px)',
            }}
          >
            <Search size={15} className="flex-shrink-0" style={{ color: theme?.icon?.neutral || '#5F6F64' }} />
            <input
              type="text"
              placeholder="Search personal info..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 bg-transparent border-none outline-none ml-2.5 text-sm"
              style={{ color: theme?.input?.text || '#111827' }}
              data-testid="personal-info-search"
            />
          </div>
          
          {/* Type Filter */}
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger 
              className="h-10 px-3 w-44 font-semibold text-sm"
              style={{
                background: theme?.input?.bg || 'rgba(255,255,255,0.72)',
                border: `1px solid ${theme?.input?.border || 'rgba(15,172,108,0.12)'}`,
                borderRadius: '12px',
                color: theme?.text?.secondary || '#4B5563',
                backdropFilter: 'blur(12px)',
              }}
              data-testid="personal-info-type-filter"
            >
              <Filter size={14} className="mr-2 flex-shrink-0" style={{ color: theme?.icon?.neutral || '#5F6F64' }} />
              <SelectValue placeholder="All Personal Info" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Personal Info</SelectItem>
              {Object.entries(FIELD_CONFIG)
                .filter(([key]) => typeCounts[key])
                .sort(([, a], [, b]) => a.order - b.order)
                .map(([key, config]) => (
                  <SelectItem key={key} value={key}>
                    {config.label}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          
          {/* Bureau Filter */}
          <Select value={bureauFilter} onValueChange={setBureauFilter}>
            <SelectTrigger 
              className="h-10 px-3 w-36 font-semibold text-sm"
              style={{
                background: theme?.input?.bg || 'rgba(255,255,255,0.72)',
                border: `1px solid ${theme?.input?.border || 'rgba(15,172,108,0.12)'}`,
                borderRadius: '12px',
                color: theme?.text?.secondary || '#4B5563',
                backdropFilter: 'blur(12px)',
              }}
              data-testid="personal-info-bureau-filter"
            >
              <SelectValue placeholder="All Bureaus" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Bureaus</SelectItem>
              <SelectItem value="TransUnion">TransUnion</SelectItem>
              <SelectItem value="Experian">Experian</SelectItem>
              <SelectItem value="Equifax">Equifax</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div 
          className="text-sm"
          style={{ color: theme?.text?.muted || '#6B7280' }}
        >
          {filteredRows.length === allRows.length 
            ? `${allRows.length} field${allRows.length === 1 ? '' : 's'}`
            : `${filteredRows.length} of ${allRows.length} field${allRows.length === 1 ? '' : 's'}`
          }
        </div>
      </div>
      
      {/* Bureau Comparison Table - Split-table design */}
      {filteredRows.length > 0 ? (
        <div 
          className="overflow-x-auto rounded-xl"
          style={{
            background: theme?.table?.container || 'rgba(255,255,255,0.84)',
            border: `1px solid ${theme?.border?.b1 || 'rgba(255,255,255,0.44)'}`,
          }}
        >
          <table className="w-full min-w-max">
            <thead>
              <tr style={{ background: theme?.table?.header || 'rgba(255,255,255,0.60)' }}>
                <th 
                  className="text-center py-3 px-3 w-12"
                  style={{ borderBottom: `1px solid ${theme?.table?.border || 'rgba(15,172,108,0.10)'}` }}
                >
                  <Checkbox 
                    checked={isAllSelected}
                    onCheckedChange={toggleSelectAll}
                    className="data-[state=checked]:bg-emerald-600"
                    data-testid="select-all-personal-info"
                  />
                </th>
                <th 
                  className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wide w-44"
                  style={{ color: theme?.text?.muted, borderBottom: `1px solid ${theme?.table?.border}` }}
                >
                  Field
                </th>
                <th 
                  className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wide"
                  style={{ color: theme?.text?.muted, borderBottom: `1px solid ${theme?.table?.border}` }}
                >
                  TransUnion
                </th>
                <th 
                  className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wide"
                  style={{ color: theme?.text?.muted, borderBottom: `1px solid ${theme?.table?.border}` }}
                >
                  Experian
                </th>
                <th 
                  className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wide"
                  style={{ color: theme?.text?.muted, borderBottom: `1px solid ${theme?.table?.border}` }}
                >
                  Equifax
                </th>
                <th 
                  className="text-left py-3 px-4 text-xs font-bold uppercase tracking-wide w-28"
                  style={{ color: theme?.text?.muted, borderBottom: `1px solid ${theme?.table?.border}` }}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row, idx) => {
                const isSelected = selectedIds.has(row.id);
                const isTraySelected = isItemSelected(CATEGORIES.PERSONAL_INFO, row.id);
                const Icon = row.icon;
                const rowBg = (isSelected || isTraySelected) 
                  ? theme?.table?.rowHover 
                  : idx % 2 === 0 
                    ? theme?.table?.row 
                    : theme?.table?.rowAlt;
                
                return (
                  <tr 
                    key={row.id}
                    className="transition-colors"
                    style={{
                      background: rowBg,
                      borderBottom: `1px solid ${theme?.table?.border}`,
                    }}
                    data-testid={`personal-info-row-${row.id}`}
                  >
                    {/* Select */}
                    <td className="py-4 px-3 text-center align-top">
                      <Checkbox 
                        checked={isSelected || isTraySelected}
                        onCheckedChange={() => toggleSelect(row)}
                        className="data-[state=checked]:bg-emerald-600"
                      />
                    </td>
                    
                    {/* Field Name */}
                    <td className="py-4 px-4 align-top">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
                            style={{ 
                              background: theme?.iconBox?.bg || 'rgba(15,172,108,0.10)',
                              border: `1px solid ${theme?.iconBox?.border || 'rgba(15,172,108,0.12)'}`,
                            }}
                          >
                            <Icon size={16} style={{ color: theme?.icon?.primary }} />
                          </div>
                          <span 
                            className="text-sm font-semibold"
                            style={{ color: theme?.text?.primary }}
                          >
                            {row.label}
                          </span>
                          {/* Employer Recommendation Badge */}
                          {row.fieldKey === 'employers' && hasEmployerData(row) && (
                            <Badge 
                              variant="outline"
                              className="bg-amber-50 text-amber-700 border-amber-300 text-xs whitespace-nowrap"
                              data-testid="employer-recommended-badge"
                            >
                              <AlertTriangle size={10} className="mr-1" />
                              Recommended to Dispute
                            </Badge>
                          )}
                        </div>
                        {/* Employer Education Tooltip */}
                        {row.fieldKey === 'employers' && hasEmployerData(row) && (
                          <div className="ml-10 text-xs text-amber-600 flex items-start gap-1 max-w-md">
                            <Info size={12} className="mt-0.5 flex-shrink-0" />
                            <span>Employer info may affect file matching and verification. Consider disputing for cleaner personal-info foundation.</span>
                          </div>
                        )}
                      </div>
                    </td>
                    
                    {/* TransUnion */}
                    <td className="py-4 px-4 align-top">
                      <BureauCell 
                        value={row.values.TransUnion} 
                        bureau="TransUnion" 
                        fieldKey={row.fieldKey} 
                        reportedDate={row.reportedDates?.TransUnion}
                        theme={theme} 
                      />
                    </td>
                    
                    {/* Experian */}
                    <td className="py-4 px-4 align-top">
                      <BureauCell 
                        value={row.values.Experian} 
                        bureau="Experian" 
                        fieldKey={row.fieldKey} 
                        reportedDate={row.reportedDates?.Experian}
                        theme={theme} 
                      />
                    </td>
                    
                    {/* Equifax */}
                    <td className="py-4 px-4 align-top">
                      <BureauCell 
                        value={row.values.Equifax} 
                        bureau="Equifax" 
                        fieldKey={row.fieldKey} 
                        reportedDate={row.reportedDates?.Equifax}
                        theme={theme} 
                      />
                    </td>
                    
                    {/* Actions */}
                    <td className="py-4 px-4 align-top">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDisputeSingle(row)}
                        data-testid={`dispute-personal-info-${row.id}`}
                        style={{ color: isTraySelected ? theme?.icon?.primary : theme?.icon?.neutral }}
                      >
                        {isTraySelected ? (
                          <>
                            <CheckCircle size={16} />
                            <span className="ml-1">Added</span>
                          </>
                        ) : (
                          <>
                            <Plus size={16} />
                            <span className="ml-1">Dispute</span>
                          </>
                        )}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div 
          className="text-center py-8"
          style={{ color: theme?.text?.muted }}
        >
          No personal information matches your filters.
        </div>
      )}
      
      {/* Bulk Action Bar */}
      <BulkActionBar
        selectedCount={selectedIds.size}
        onDispute={handleBulkDispute}
        onClear={clearSelection}
      />
    </div>
  );
};

export default PersonalInfoTable;
